import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowDownTrayIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowUturnLeftIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  LockClosedIcon,
  LockOpenIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useDispatch, useSelector } from '../../../Flux/index.js'
import {
  chatSlice,
  selectItemMeta,
  selectNotes,
  selectPinnedItemIds
} from '../../../Flux/Slice/Chat/ChatSlice.js'
import { cubeSourceId, directiveKey } from '../../../Flux/Slice/Chat/chatViz.js'
import { visualizationSlice } from '../../../Flux/Slice/Visualization/VisualizationSlice.js'
import { deriveWorkspaceItems, partitionByPinned } from '../../../Flux/Slice/Chat/workspaceItems.js'
import type { WorkspaceItem } from '../../../Flux/Slice/Chat/workspaceItems.js'
import type { ComparisonCaveat } from '../../../Generated/ComparisonCaveat.ts'
import { askAboutStarter, GraphPanel, ProvenanceChip } from './ChatVizBubble.js'
import { ReportPanel } from './ReportPanel.js'

/**
 * W-2 — the ONE unified "Workspace" panel that replaces the old Charts | Report tab switcher. It renders a
 * single scrollable list of item-cards derived from EXISTING slice state (see {@link deriveWorkspaceItems}):
 * one card per open chart (wrapping {@link GraphPanel}), one card for the current report (wrapping
 * {@link ReportPanel}), and N manual NOTE cards. Each card WRAPS the existing rendering — it never
 * reimplements it — and adds a pin toggle, a maximize control, and an editable title/subtitle.
 *
 * Layout: a Pinned section (top, user-committed items) then a Staged section. "Add note" mints a blank note.
 * Maximize expands a card into a fullscreen overlay (portaled) showing the SAME wrapped content, larger.
 *
 * Deferred to W-2b (not built here): per-report-SECTION cards (the report stays ONE card); the backend "agent
 * cannot modify a PINNED item" enforcement (the frontend only records the pin).
 */
export function WorkspacePanel() {
  const dispatch = useDispatch()
  const openGraphs = useSelector((s) => s.chat.openGraphs)
  const report = useSelector((s) => s.chat.currentReport)
  const notes = useSelector(selectNotes)
  const pinnedItemIds = useSelector(selectPinnedItemIds)
  const itemMeta = useSelector(selectItemMeta)
  const focusedGraphKey = useSelector((s) => s.chat.focusedGraphKey)
  const comparisonCaveat = useSelector((s) => s.chat.comparisonCaveat)

  const items = useMemo(
    () => deriveWorkspaceItems({ openGraphs, report, notes, pinnedItemIds, itemMeta }),
    [openGraphs, report, notes, pinnedItemIds, itemMeta]
  )
  const { pinned, staged } = useMemo(() => partitionByPinned(items), [items])

  // The item currently expanded to the fullscreen overlay (by id). Cleared if the item disappears (e.g. its
  // chart was closed) so we never render a dangling overlay.
  const [maximizedId, setMaximizedId] = useState<string | undefined>(undefined)
  const maximizedItem = items.find((i) => i.id === maximizedId)
  useEffect(() => {
    if (maximizedId && !maximizedItem) setMaximizedId(undefined)
  }, [maximizedId, maximizedItem])

  const isEmpty = items.length === 0

  const onAddNote = () => dispatch(chatSlice.actions.addNote({}))

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 px-3 pt-3">
        <span className="text-sm font-semibold opacity-70">Workspace</span>
        <button type="button" className="btn btn-ghost btn-xs gap-1" onClick={onAddNote} title="Add a manual note">
          <PlusIcon className="w-4" /> Add note
        </button>
      </div>

      <div className="flex min-h-0 grow flex-col gap-3 overflow-y-auto p-3">
        {comparisonCaveat && <ComparisonCaveatBanner caveat={comparisonCaveat} />}

        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-gray-400">
            <DocumentTextIcon className="h-8 w-8 opacity-50" />
            <span>Charts, reports, and notes you create appear here.</span>
          </div>
        ) : (
          <>
            {pinned.length > 0 && (
              <Section label="Pinned">
                {pinned.map((item) => (
                  <WorkspaceItemCard
                    key={item.id}
                    item={item}
                    focusedGraphKey={focusedGraphKey}
                    onMaximize={() => setMaximizedId(item.id)}
                  />
                ))}
              </Section>
            )}
            <Section label={pinned.length > 0 ? 'Staged' : undefined}>
              {staged.length === 0 ? (
                <p className="px-1 text-xs opacity-50">Nothing staged — everything here is pinned.</p>
              ) : (
                staged.map((item) => (
                  <WorkspaceItemCard
                    key={item.id}
                    item={item}
                    focusedGraphKey={focusedGraphKey}
                    onMaximize={() => setMaximizedId(item.id)}
                  />
                ))
              )}
            </Section>
          </>
        )}
      </div>

      {maximizedItem &&
        createPortal(
          <MaximizedOverlay item={maximizedItem} onClose={() => setMaximizedId(undefined)} />,
          document.body
        )}
    </div>
  )
}

/** A labelled group of cards. An unlabelled section renders just its children (the sole staged list). */
function Section({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      {label && <div className="px-1 text-xs font-semibold uppercase tracking-wide opacity-50">{label}</div>}
      {children}
    </div>
  )
}

/** Colour a comparison caveat by its confidence band (enable-not-veto: a caveat, never an error). */
function caveatClasses(confidence: string): string {
  switch (confidence.toUpperCase()) {
    case 'HIGH':
      return 'border-green-400 bg-green-50 text-green-800'
    case 'LOW':
      return 'border-red-400 bg-red-50 text-red-800'
    default:
      return 'border-amber-400 bg-amber-50 text-amber-800'
  }
}

/** The server-computed comparison verdict banner (carried over from the old GraphCanvas so nothing regresses). */
function ComparisonCaveatBanner({ caveat }: { caveat: ComparisonCaveat }) {
  const score = caveat.confidenceScore != null ? ` (${caveat.confidenceScore.toFixed(2)})` : ''
  return (
    <div className={`shrink-0 rounded border px-2 py-1 text-xs ${caveatClasses(caveat.confidence)}`}>
      <span className="font-bold">
        {caveat.confidence} confidence{score}
      </span>
      <span className="ml-1">{caveat.caveat}</span>
    </div>
  )
}

/**
 * One workspace item-card: the card chrome (editable title/subtitle · pin · maximize · note-delete) wrapping the
 * existing rendering for the item's kind. The chart card highlights when it is the focused graph.
 */
function WorkspaceItemCard({
  item,
  focusedGraphKey,
  onMaximize
}: {
  item: WorkspaceItem
  focusedGraphKey?: string
  onMaximize: () => void
}) {
  const chartFocused = item.kind === 'chart' && directiveKey(item.ref.directive) === focusedGraphKey
  // The wrapped panel renders bare (no header of its own); it publishes its export / print handler here so the
  // ONE card header can host that action (U3 — single header, panel actions hoisted up).
  const exportChartRef = useRef<(() => void) | null>(null)
  const printReportRef = useRef<(() => void) | null>(null)
  return (
    <div
      className={`rounded-xl border bg-base-100 p-3 transition-shadow ${
        chartFocused ? 'border-primary ring-2 ring-primary/40' : item.pinned ? 'border-primary/40' : 'border-gray-200'
      }`}
    >
      <CardHeader
        item={item}
        onMaximize={onMaximize}
        exportChartRef={exportChartRef}
        printReportRef={printReportRef}
      />
      <div className="mt-2">
        <ItemBody
          item={item}
          focused={chartFocused}
          exportChartRef={exportChartRef}
          printReportRef={printReportRef}
        />
      </div>
    </div>
  )
}

/** The card's top strip: editable title + subtitle, then the hoisted panel actions · pin · maximize (+ delete). */
function CardHeader({
  item,
  onMaximize,
  exportChartRef,
  printReportRef
}: {
  item: WorkspaceItem
  onMaximize: () => void
  exportChartRef: React.MutableRefObject<(() => void) | null>
  printReportRef: React.MutableRefObject<(() => void) | null>
}) {
  const dispatch = useDispatch()

  const commitTitle = (title: string) => {
    if (item.kind === 'note') dispatch(chatSlice.actions.updateNote({ id: item.ref.note.id, title }))
    else dispatch(chatSlice.actions.setItemMeta({ id: item.id, title }))
  }
  const commitSubtitle = (subtitle: string) => {
    if (item.kind === 'note') dispatch(chatSlice.actions.updateNote({ id: item.ref.note.id, subtitle }))
    else dispatch(chatSlice.actions.setItemMeta({ id: item.id, subtitle }))
  }

  const titlePlaceholder = item.kind === 'note' ? 'Note title…' : 'Card title…'

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 grow">
        <div className="flex items-center gap-1">
          {item.pinned && (
            <LockClosedIcon
              className="w-3.5 shrink-0 text-primary"
              title="Pinned — the assistant can't change this item"
              aria-label="Pinned and locked from assistant edits"
            />
          )}
          <EditableInline
            value={item.title}
            placeholder={titlePlaceholder}
            onCommit={commitTitle}
            inputClass="w-full truncate bg-transparent text-sm font-semibold outline-none placeholder:font-normal placeholder:opacity-40"
          />
        </div>
        <EditableInline
          value={item.subtitle}
          placeholder="Add a subtitle…"
          onCommit={commitSubtitle}
          inputClass="w-full truncate bg-transparent text-xs opacity-70 outline-none placeholder:opacity-40"
        />
        {item.kind !== 'note' && (
          // Per-item note/context (W-3): free text the user attaches to this card ("why this view matters").
          // Uncontrolled + commit-on-blur (like EditableInline); keyed on the stored value so an external change
          // (e.g. a loaded workspace) re-syncs. Persists via setItemMeta → itemMeta → the workspace blob.
          <textarea
            key={item.note ?? ''}
            defaultValue={item.note ?? ''}
            rows={1}
            placeholder="Add a note / context…"
            className="mt-1 w-full resize-y rounded bg-transparent text-xs italic opacity-80 outline-none placeholder:not-italic placeholder:opacity-40"
            onBlur={(e) => {
              const v = e.target.value.trim()
              if (v !== (item.note ?? '')) dispatch(chatSlice.actions.setItemMeta({ id: item.id, note: v }))
            }}
          />
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {item.kind === 'chart' && <ProvenanceChip directive={item.ref.directive} />}
        {item.kind === 'chart' && item.sourceNodeId && (
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={() => dispatch(chatSlice.actions.setCurrentNode({ nodeId: item.sourceNodeId! }))}
            title="Go to the chat turn that created this chart"
            aria-label="Go to the chat turn that created this chart"
          >
            <ChatBubbleLeftRightIcon className="w-4" />
          </button>
        )}
        {item.pinned && item.kind === 'chart' && (
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={() =>
              dispatch(
                visualizationSlice.actions.restorePinnedShared({ sourceId: cubeSourceId(item.ref.directive) })
              )
            }
            title="Restore to the pinned view (undo zoom/config changes since you pinned it)"
            aria-label="Restore this chart to its pinned view"
          >
            <ArrowUturnLeftIcon className="w-4" />
          </button>
        )}
        <ItemActions item={item} exportChartRef={exportChartRef} printReportRef={printReportRef} />
        <PinButton item={item} />
        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={onMaximize}
          title="Maximize this card"
          aria-label="Maximize this card"
        >
          <ArrowsPointingOutIcon className="w-4" />
        </button>
        {item.kind === 'note' && (
          <button
            type="button"
            className="btn btn-ghost btn-xs text-error"
            onClick={() => dispatch(chatSlice.actions.removeNote({ id: item.ref.note.id }))}
            title="Delete this note"
            aria-label="Delete this note"
          >
            <TrashIcon className="w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

/** The pin toggle — a filled bookmark when pinned, outline when staged. */
function PinButton({ item }: { item: WorkspaceItem }) {
  const dispatch = useDispatch()
  // Live shared-state of this chart's source, captured as the pinned view on pin (undefined for non-charts).
  const shared = useSelector((s) =>
    item.kind === 'chart' ? s.visualization.sources[cubeSourceId(item.ref.directive)]?.shared : undefined
  )
  return (
    <button
      type="button"
      className={`btn btn-ghost btn-xs ${item.pinned ? 'text-primary' : ''}`}
      onClick={() => {
        // Capture the current view as the pinned state (chart cards): snapshot into the source for in-session
        // "restore to pinned", AND persist it in itemMeta so it survives reload. Do this BEFORE the pin flips.
        if (item.kind === 'chart') {
          const sourceId = cubeSourceId(item.ref.directive)
          if (!item.pinned) {
            dispatch(visualizationSlice.actions.snapshotPinnedShared({ sourceId }))
            if (shared) dispatch(chatSlice.actions.setItemPinnedView({ id: item.id, view: shared }))
          } else {
            dispatch(chatSlice.actions.setItemPinnedView({ id: item.id })) // unpin → drop the persisted view
          }
        }
        dispatch(chatSlice.actions.togglePinItem({ id: item.id }))
      }}
      aria-pressed={item.pinned}
      title={
        item.pinned
          ? "Pinned — the assistant can't change this. Click to unpin."
          : "Pin (locks it so the assistant can't change it)"
      }
      aria-label={
        item.pinned
          ? "Pinned — the assistant can't change this. Click to unpin."
          : "Pin this item (locks it so the assistant can't change it)"
      }
    >
      {item.pinned ? <LockClosedIcon className="w-4" /> : <LockOpenIcon className="w-4" />}
    </button>
  )
}

/**
 * The panel actions hoisted OUT of the wrapped panel's (now-dropped) header into the ONE card header (U3):
 * chart → ask · export PNG · close; report → print. Icon-only to sit compactly beside pin/maximize; the export
 * and print handlers are supplied by the wrapped bare panel via the shared refs.
 */
function ItemActions({
  item,
  exportChartRef,
  printReportRef
}: {
  item: WorkspaceItem
  exportChartRef: React.MutableRefObject<(() => void) | null>
  printReportRef: React.MutableRefObject<(() => void) | null>
}) {
  const dispatch = useDispatch()
  if (item.kind === 'chart') {
    const directive = item.ref.directive
    return (
      <>
        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={() => dispatch(chatSlice.actions.seedComposer({ text: askAboutStarter(directive) }))}
          title="Ask the assistant about this chart"
          aria-label="Ask about this chart"
        >
          <ChatBubbleLeftRightIcon className="w-4" />
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={() => exportChartRef.current?.()}
          title="Export this chart as PNG"
          aria-label="Export this chart as PNG"
        >
          <ArrowDownTrayIcon className="w-4" />
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={() => dispatch(chatSlice.actions.closeGraph({ key: directiveKey(directive) }))}
          title="Close this chart"
          aria-label="Close chart"
        >
          <XMarkIcon className="w-4" />
        </button>
      </>
    )
  }
  if (item.kind === 'report') {
    return (
      <button
        type="button"
        className="btn btn-ghost btn-xs"
        onClick={() => printReportRef.current?.()}
        title="Print or save this report as a PDF"
        aria-label="Print or save this report as a PDF"
      >
        <PrinterIcon className="w-4" />
      </button>
    )
  }
  return null
}

/** Renders the WRAPPED existing content for an item. `maximized` gives the report/note more height in the overlay. */
function ItemBody({
  item,
  focused,
  maximized,
  exportChartRef,
  printReportRef
}: {
  item: WorkspaceItem
  focused?: boolean
  maximized?: boolean
  exportChartRef: React.MutableRefObject<(() => void) | null>
  printReportRef: React.MutableRefObject<(() => void) | null>
}) {
  switch (item.kind) {
    case 'chart':
      // Bare (no header of its own); the card header hosts ask/export/close. `exportChartRef` carries the
      // export handler up so the header's Export button reaches it.
      return <GraphPanel directive={item.ref.directive} focused={focused} bare exportRef={exportChartRef} />
    case 'report':
      // ReportPanel is h-full; give it a bounded height in the stacked list (full height when maximized).
      return (
        <div className={maximized ? 'h-full' : 'h-[560px]'}>
          <ReportPanel bare printRef={printReportRef} />
        </div>
      )
    case 'note':
      return <NoteBody note={item.ref.note} maximized={maximized} />
  }
}

/** The editable body of a NOTE card: a plain textarea committed on blur (per-keystroke dispatch would be noisy). */
function NoteBody({ note, maximized }: { note: { id: string; text: string }; maximized?: boolean }) {
  const dispatch = useDispatch()
  return (
    <textarea
      key={note.id}
      defaultValue={note.text}
      placeholder="Write a note… (saved when you click away)"
      onBlur={(e) => {
        if (e.target.value !== note.text) dispatch(chatSlice.actions.updateNote({ id: note.id, text: e.target.value }))
      }}
      className={`textarea textarea-bordered w-full resize-none text-sm ${maximized ? 'h-full' : 'min-h-[120px]'}`}
    />
  )
}

/**
 * Inline-editable text: looks like plain text, becomes editable on focus, commits on blur / Enter. Kept in sync
 * with the store value while unfocused so an external change (e.g. resume) is reflected without clobbering an
 * in-progress edit.
 */
function EditableInline({
  value,
  placeholder,
  onCommit,
  inputClass
}: {
  value?: string
  placeholder: string
  onCommit: (v: string) => void
  inputClass: string
}) {
  const [draft, setDraft] = useState(value ?? '')
  const focused = useRef(false)
  useEffect(() => {
    if (!focused.current) setDraft(value ?? '')
  }, [value])

  const commit = () => {
    if ((draft ?? '') !== (value ?? '')) onCommit(draft)
  }

  return (
    <input
      type="text"
      value={draft}
      placeholder={placeholder}
      onFocus={() => {
        focused.current = true
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        focused.current = false
        commit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          e.currentTarget.blur()
        }
      }}
      title="Click to rename"
      // Subtle "this is editable" affordance: a dotted underline on hover (the field otherwise looks like plain text).
      className={`${inputClass} cursor-text rounded hover:underline hover:decoration-dotted hover:decoration-base-content/40 hover:underline-offset-2`}
    />
  )
}

/** The fullscreen overlay for a maximized card — same wrapped content, larger. Closes on backdrop / Escape / ✕. */
function MaximizedOverlay({ item, onClose }: { item: WorkspaceItem; onClose: () => void }) {
  const chartFocused = item.kind === 'chart'
  const exportChartRef = useRef<(() => void) | null>(null)
  const printReportRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex h-full max-h-[92vh] w-full max-w-6xl flex-col rounded-2xl border border-gray-200 bg-base-100 p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex shrink-0 items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-base font-semibold" title={item.title}>
              {item.title ?? 'Untitled'}
            </div>
            {item.subtitle && <div className="truncate text-xs opacity-70">{item.subtitle}</div>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {item.kind === 'chart' && <ProvenanceChip directive={item.ref.directive} />}
            <ItemActions item={item} exportChartRef={exportChartRef} printReportRef={printReportRef} />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onClose}
              title="Minimize"
              aria-label="Minimize this card"
            >
              <ArrowsPointingInIcon className="w-4" />
            </button>
          </div>
        </div>
        <div className="min-h-0 grow overflow-auto">
          <ItemBody
            item={item}
            focused={chartFocused}
            maximized
            exportChartRef={exportChartRef}
            printReportRef={printReportRef}
          />
        </div>
      </div>
    </div>
  )
}
